import React, { useState, useEffect, useRef } from 'react';
import './Game.css';
import { playTingSound, playGameOverSound } from './audioUtils';

// Constants for physics - EXTREME SPEED
const GRAVITY = 0.2;            // EXTREMELY fast falling
const JUMP_FORCE = -3.6;         // EXTREMELY powerful jump
const PIPE_SPEED_INITIAL = 1.5; // Actual initial speed is 3.0x
const PIPE_SPEED_INCREMENT = 0.3; // Adjusted to show 0.1x speed increment
const PIPE_SPEED_MAX = 20;      // VERY high maximum speed
const PIPE_GAP = 130;           // Initial gap between pipes
const PIPE_GAP_MIN = 100;       // Minimum gap between pipes (reduced from 180)
const PIPE_GAP_REDUCTION_RATE = 2; // Reduce gap by 2px every point after score 20
const SPEED_UPDATE_INTERVAL = 5000; // Increase speed every 5 seconds

// Add a constant for speed display conversion (3.0 actual = 1.0 displayed)
const SPEED_DISPLAY_DIVISOR = 1.5;

// Game dimensions for collision detection
const GAME_HEIGHT = 570;
const BIRD_HEIGHT = 24;
const BIRD_WIDTH = 34;
const BIRD_X_POSITION = 60;
const GROUND_HEIGHT = 48;

// Add a new constant - position of the visible ground top:
const GROUND_TOP_POSITION = GAME_HEIGHT - GROUND_HEIGHT; // Position of the top of the visible ground

// Add function to detect if device is small screen
const isSmallDevice = () => {
    return window.innerWidth <= 768 || window.innerHeight <= 600;
};

// Add function to detect Android devices specifically
const isAndroidDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('android');
};

// Add function to detect iOS devices specifically
const isIOSDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod') || 
           (userAgent.includes('macintosh') && 'ontouchend' in document);
};

// Add function to detect if device is iPad
const isIPad = () => {
    // Check for iPad using user agent and screen dimensions
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPadUserAgent = userAgent.includes('ipad') || 
                           (userAgent.includes('macintosh') && 'ontouchend' in document);
    
    // Check for iPad-like screen dimensions
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const isIPadDimensions = (
        (screenWidth === 768 && screenHeight === 1024) ||  // iPad standard
        (screenWidth === 1024 && screenHeight === 768) ||  // iPad landscape
        (screenWidth === 820 && screenHeight === 1180) ||  // iPad Air
        (screenWidth === 1180 && screenHeight === 820) ||  // iPad Air landscape
        (screenWidth === 834 && screenHeight === 1194) ||  // iPad Pro 11"
        (screenWidth === 1194 && screenHeight === 834) ||  // iPad Pro 11" landscape
        (screenWidth === 1024 && screenHeight === 1366) || // iPad Pro 12.9"
        (screenWidth === 1366 && screenHeight === 1024)    // iPad Pro 12.9" landscape
    );
    
    return isIPadUserAgent || isIPadDimensions;
};

const isPortraitMode = () => {
    return window.innerHeight > window.innerWidth;
};

// Add function to get actual game dimensions based on device
const getGameDimensions = () => {
    const isSmall = isSmallDevice();
    
    if (isSmall) {
        // For small devices, use the CSS dimensions
        if (window.innerWidth <= 480) {
            // Mobile portrait: 320x480
            return {
                width: 320,
                height: 480,
                groundHeight: Math.round(48 * (480 / 570)), // Scale ground height proportionally
                isSmallDevice: true
            };
        } else if (window.innerWidth <= 768 && window.innerHeight <= 500) {
            // Mobile landscape: 320x380
            return {
                width: 320,
                height: 380,
                groundHeight: Math.round(48 * (380 / 570)), // Scale ground height proportionally
                isSmallDevice: true
            };
        } else if (window.innerHeight <= 600) {
            // Height-constrained devices: 320x380
            return {
                width: 320,
                height: 380,
                groundHeight: Math.round(48 * (380 / 570)), // Scale ground height proportionally
                isSmallDevice: true
            };
        }
    }
    
    // Default desktop dimensions
    return {
        width: 380,
        height: 570,
        groundHeight: 48,
        isSmallDevice: false
    };
};

// Enable debug mode temporarily for testing
const HITBOX_BUFFER = 18; // Extremely large buffer to make hitbox tiny
const DEBUG_MODE = true; // Set to true to see collision boxes during testing

// Change the constant to re-enable bottom pipe collisions
const DISABLE_BOTTOM_PIPE_COLLISIONS = false; // Set to false to enable bottom pipe collisions

// Add a small visual tolerance for collision detection
const COLLISION_TOLERANCE = 3; // 3 pixels tolerance for visual accuracy

// Generate color for a specific level
const getLevelColor = (level) => {
    // Create a rainbow cycle of colors
    const hue = (level * 15) % 360;
    return `hsl(${hue}, 100%, 50%)`;
};

// Calculate dynamic pipe gap based on pipes crossed (score)
const getDynamicPipeGap = (pipesCrossed) => {
    // Reduce gap every 20 pipes crossed
    const gapReductionLevel = Math.floor(pipesCrossed / 20);
    
    if (gapReductionLevel === 0) {
        return PIPE_GAP; // Use initial gap for first 20 pipes
    }
    
    // After every 20 pipes, reduce gap by PIPE_GAP_REDUCTION_RATE pixels
    const reductionAmount = gapReductionLevel * PIPE_GAP_REDUCTION_RATE;
    const newGap = PIPE_GAP - reductionAmount;
    
    // Don't let gap go below minimum
    return Math.max(newGap, PIPE_GAP_MIN);
};

// Social media SVG icons
const SocialIcons = {
    whatsapp: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
        </svg>
    ),
    telegram: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512" width="16" height="16" fill="currentColor">
            <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z"/>
        </svg>
    ),
    twitter: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" fill="currentColor">
            <path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"/>
        </svg>
    ),
    facebook: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" fill="currentColor">
            <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"/>
        </svg>
    ),
    email: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" fill="currentColor">
            <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"/>
        </svg>
    )
};

// Game header icons
const HeaderIcons = {
    bird: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="24" height="24" fill="#FFD700">
            <path d="M503.2 393.8L280.1 44.25c-10.42-16.33-37.73-16.33-48.15 0L8.807 393.8c-11.11 17.41-11.75 39.42-1.666 57.45C17.07 468.1 35.92 480 56.31 480h399.4c20.39 0 39.24-11.03 49.18-28.77C514.9 433.2 514.3 411.2 503.2 393.8zM256 111.8L327.8 224H256L208 288L177.2 235.3L256 111.8z"/>
        </svg>
    ),
    leaderboard: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="20" height="20" fill="currentColor">
            <path d="M572.1 82.38C569.5 71.59 559.8 64 548.7 64h-100.8c.2422-12.45 .1078-23.7-.1559-33.02C447.3 13.63 433.2 0 415.8 0H160.2C142.8 0 128.7 13.63 128.2 30.98C127.1 40.3 127.8 51.55 128.1 64H27.26C16.16 64 6.537 71.59 3.912 82.38C3.1 85.78-15.71 167.2 37.07 245.9c37.44 55.82 100.6 95.03 187.5 117.4c18.7 4.805 31.41 22.06 31.41 41.37C256 428.5 236.5 448 212.6 448H208c-26.51 0-47.99 21.49-47.99 48c0 8.836 7.163 16 15.1 16h223.1c8.836 0 15.1-7.164 15.1-16c0-26.51-21.48-48-47.99-48h-4.644c-23.86 0-43.36-19.5-43.36-43.35c0-19.31 12.71-36.57 31.41-41.37c86.96-22.34 150.1-61.55 187.5-117.4C591.7 167.2 572.9 85.78 572.1 82.38zM77.41 219.8C49.47 178.6 47.01 135.7 48.38 112h80.39c5.359 59.62 20.35 131.1 57.67 189.1C137.4 281.6 100.9 254.4 77.41 219.8zM498.6 219.8c-23.44 34.6-59.94 61.75-109 81.22C426.9 243.1 441.9 171.6 447.2 112h80.39C528.1 135.7 526.5 178.7 498.6 219.8z"/>
        </svg>
    ),
    policy: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="18" height="18" fill="currentColor">
            <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272 416h-160C103.2 416 96 408.8 96 400C96 391.2 103.2 384 112 384h160c8.836 0 16 7.162 16 16C288 408.8 280.8 416 272 416zM272 352h-160C103.2 352 96 344.8 96 336C96 327.2 103.2 320 112 320h160c8.836 0 16 7.162 16 16C288 344.8 280.8 352 272 352zM288 272C288 280.8 280.8 288 272 288h-160C103.2 288 96 280.8 96 272C96 263.2 103.2 256 112 256h160C280.8 256 288 263.2 288 272z"/>
        </svg>
    ),
    about: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18" fill="currentColor">
            <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z"/>
        </svg>
    ),
    keyboard: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width="16" height="16" fill="currentColor">
            <path d="M512 64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h448c35.35 0 64-28.65 64-64V128C576 92.65 547.3 64 512 64zM528 384c0 8.822-7.178 16-16 16H64c-8.822 0-16-7.178-16-16V128c0-8.822 7.178-16 16-16h448c8.822 0 16 7.178 16 16V384zM140 152h-24c-6.656 0-12 5.344-12 12v24c0 6.656 5.344 12 12 12h24c6.656 0 12-5.344 12-12v-24C152 157.3 146.7 152 140 152zM196 200h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C184 194.7 189.3 200 196 200zM276 200h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C264 194.7 269.3 200 276 200zM356 200h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C344 194.7 349.3 200 356 200zM460 152h-24c-6.656 0-12 5.344-12 12v24c0 6.656 5.344 12 12 12h24c6.656 0 12-5.344 12-12v-24C472 157.3 466.7 152 460 152zM140 232h-24c-6.656 0-12 5.344-12 12v24c0 6.656 5.344 12 12 12h24c6.656 0 12-5.344 12-12v-24C152 237.3 146.7 232 140 232zM196 280h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C184 274.7 189.3 280 196 280zM276 280h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C264 274.7 269.3 280 276 280zM356 280h24c6.656 0 12-5.344 12-12v-24c0-6.656-5.344-12-12-12h-24c-6.656 0-12 5.344-12 12v24C344 274.7 349.3 280 356 280zM460 232h-24c-6.656 0-12 5.344-12 12v24c0 6.656 5.344 12 12 12h24c6.656 0 12-5.344 12-12v-24C472 237.3 466.7 232 460 232zM400 320h-224C167.2 320 160 327.2 160 336V352c0 8.836 7.164 16 16 16h224c8.836 0 16-7.164 16-16v-16C416 327.2 408.8 320 400 320z"/>
        </svg>
    )
};

// Social sharing function
const shareScore = (score) => {
    const shareMessage = `Hey! I scored ${score} points in Flappy Bird! Can you beat my score? Try it yourself and share your highest score with the world!! 🐦`;
    const shareUrl = window.location.href;
    return {
        whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage + ' ' + shareUrl)}`,
        telegram: `https://telegram.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareMessage)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}&url=${encodeURIComponent(shareUrl)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareMessage)}`,
        email: `mailto:?subject=${encodeURIComponent('My Flappy Bird Score!')}&body=${encodeURIComponent(shareMessage + '\n\n' + shareUrl)}`
    };
};

// Add function to detect Samsung Android devices
const isSamsungAndroid = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('android') && userAgent.includes('samsung');
};

const Game = ({ navigateTo }) => {
    const [gameStarted, setGameStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [birdY, setBirdY] = useState(240);
    const [birdVelocity, setBirdVelocity] = useState(0);
    const [birdRotation, setBirdRotation] = useState(0);
    const [pipes, setPipes] = useState([]);
    const [pipeSpeed, setPipeSpeed] = useState(PIPE_SPEED_INITIAL);
    const [speedLevel, setSpeedLevel] = useState(1.0); // New state to track displayed speed level
    const [speedChanged, setSpeedChanged] = useState(false); // Track when speed changes
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [bottomPipeCollisionsEnabled, setBottomPipeCollisionsEnabled] = useState(!DISABLE_BOTTOM_PIPE_COLLISIONS); // State to track bottom pipe collisions
    const [allCollisionsEnabled, setAllCollisionsEnabled] = useState(true); // State to track if all collisions are enabled
    const [dynamicTolerance, setDynamicTolerance] = useState(15); // Dynamic collision tolerance
    const [showIPadAlert, setShowIPadAlert] = useState(false);
    const [prevBottomPipeHeight, setPrevBottomPipeHeight] = useState(null);

    // References
    const frameRef = useRef();
    const pipeIntervalRef = useRef();
    const speedIntervalRef = useRef();
    const birdRef = useRef(null);
    const velocityRef = useRef(0);
    const positionRef = useRef(240);
    const pipeSpeedRef = useRef(PIPE_SPEED_INITIAL);
    const canvasRef = useRef(null); // Add a reference for the canvas
    
    // Add a frame counter reference for optimization
    const frameCountRef = useRef(0);
    
    // Create a ref for the rendering context
    const ctxRef = useRef(null);
    const gameLoopRef = useRef(null);

    // Initialize canvas and context when component mounts
    useEffect(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const gameDimensions = getGameDimensions();
            canvas.width = gameDimensions.width; // Use dynamic width
            canvas.height = gameDimensions.height; // Use dynamic height
            const context = canvas.getContext('2d');
            ctxRef.current = context;
        }
    }, []);

    // Update canvas dimensions when window resizes
    useEffect(() => {
        const handleResize = () => {
            if (canvasRef.current) {
                const canvas = canvasRef.current;
                const gameDimensions = getGameDimensions();
                canvas.width = gameDimensions.width;
                canvas.height = gameDimensions.height;
                ctxRef.current = canvas.getContext('2d');
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Clear canvas when debug mode is toggled
    useEffect(() => {
        if (ctxRef.current) {
            const ctx = ctxRef.current;
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
    }, [showDebug]);
    
    // Gradually increase pipe speed over time
    useEffect(() => {
        if (!gameStarted || gameOver) return;
        
        // Set up interval to increase speed by 0.1x every 5 seconds
        console.log("Setting up speed increase interval - starting at 1.0x speed, increasing by 0.1x every 5 seconds");
        
        // Initialize speed level to 1.0
        setSpeedLevel(1.0);
        
        speedIntervalRef.current = setInterval(() => {
            // Update pipe speed for game physics
            setPipeSpeed(prevSpeed => {
                const newSpeed = Math.min(prevSpeed + PIPE_SPEED_INCREMENT, PIPE_SPEED_MAX);
                pipeSpeedRef.current = newSpeed; // Update ref for immediate use in game loop
                return newSpeed;
            });
            
            // Update display speed level by exactly 0.1
            setSpeedLevel(prevLevel => {
                const newLevel = Math.round((prevLevel + 0.1) * 10) / 10; // Ensure exact 0.1 increment
                console.log(`Speed increased to ${newLevel.toFixed(1)}x`);
                
                // Trigger visual effect when speed changes
                setSpeedChanged(true);
                setTimeout(() => setSpeedChanged(false), 1000);
                
                return newLevel;
            });
        }, SPEED_UPDATE_INTERVAL);
        
        return () => {
            clearInterval(speedIntervalRef.current);
        };
    }, [gameStarted, gameOver]);

    // Game loop
    useEffect(() => {
        // Skip if game isn't started
        if (!gameStarted || gameOver) return;
        
        console.log("Game started, animation loop running");
        
        // Simple animation loop
        const runGameLoop = () => {
            // Get current game dimensions based on device
            const gameDimensions = getGameDimensions();
            const currentGameHeight = gameDimensions.height;
            const currentGroundHeight = gameDimensions.groundHeight;
            const currentGroundTop = currentGameHeight - currentGroundHeight;
            
            // Apply gravity to velocity - adjust for device type
            let effectiveGravity = GRAVITY;
            
            // Reduce gravity for Samsung Android devices
            if (isSamsungAndroid()) {
                effectiveGravity = GRAVITY * 0.5; // 50% reduction for Samsung Android
                velocityRef.current *= 0.85; // Add velocity dampening
            } else if (isAndroidDevice()) {
                effectiveGravity = GRAVITY * 0.7; // Original 30% reduction for other Android devices
            } else if (isIOSDevice() && isSmallDevice()) {
                effectiveGravity = GRAVITY * 0.7; // Original reduction for iOS mobile
            } else if (isSmallDevice()) {
                effectiveGravity = GRAVITY * 0.7; // Original reduction for other mobile
            }
            
            velocityRef.current += effectiveGravity;
            
            // Apply velocity to position
            positionRef.current += velocityRef.current;
            
            // Keep bird within bounds - don't let it go above the screen
            if (positionRef.current < 0) {
                positionRef.current = 0;
                velocityRef.current = 0;
            }
            
            // UPDATED: Ground collision detection using dynamic dimensions
            // Use the actual game height for the current device
            const birdBottomPosition = positionRef.current + BIRD_HEIGHT;
            
            // Ground collision - use current ground position
            if (birdBottomPosition > currentGroundTop) {
                console.log(`GROUND COLLISION - Device: ${gameDimensions.isSmallDevice ? 'SMALL' : 'LARGE'}, Game Height: ${currentGameHeight}, Ground Top: ${currentGroundTop}, Bird Bottom: ${birdBottomPosition}`);
                positionRef.current = currentGroundTop - BIRD_HEIGHT;
                velocityRef.current = 0;
                handleGameOver();
            }
            
            // Update visual position
            if (birdRef.current) {
                birdRef.current.style.top = `${positionRef.current}px`;
                birdRef.current.style.transform = `rotate(${velocityRef.current * 3}deg)`;
            }
            
            // Optimize state updates - only update every few frames
            frameCountRef.current = (frameCountRef.current + 1) % 3;
            if (frameCountRef.current === 0) {
                setBirdY(positionRef.current);
                setBirdVelocity(velocityRef.current);
                setBirdRotation(velocityRef.current * 3);
            }
            
            // Use the current pipe speed from ref for immediate updates
            const currentPipeSpeed = pipeSpeedRef.current;
            
            // Move pipes with adjusted speed for Samsung Android
            setPipes(prevPipes => {
                const speedMultiplier = isSamsungAndroid() ? 1.5 : 2.5; // Reduced speed for Samsung Android
                const newPipes = prevPipes
                    .map(pipe => ({ 
                        ...pipe, 
                        x: pipe.x - (currentPipeSpeed * speedMultiplier)
                    }))
                    .filter(pipe => pipe.x > -52);
                    
                // Only check nearby pipes to optimize
                const nearbyPipes = newPipes.filter(pipe => 
                    // Check pipes that are close to the bird (within 100px on either side)
                    Math.abs(pipe.x - BIRD_X_POSITION) < 100
                );
                
                // Create a very small hitbox for the bird (center area only)
                const birdHitbox = {
                    x: BIRD_X_POSITION,
                    y: positionRef.current,
                    width: BIRD_WIDTH,
                    height: BIRD_HEIGHT
                };
                
                // Check each nearby pipe with improved collision detection
                for (const pipe of nearbyPipes) {
                    // Skip pipes that aren't horizontally aligned with bird
                    const pipeWidth = 52; // Standard pipe width
                    
                    // Only check collision if bird and pipe overlap horizontally
                    if (BIRD_X_POSITION + BIRD_WIDTH < pipe.x || BIRD_X_POSITION > pipe.x + pipeWidth) {
                        continue; // No horizontal overlap, skip collision check
                    }
                    
                    // UPDATED: Use dynamic dimensions for pipe collision detection
                    // Get precise visual boundaries of the bird and pipes
                    const birdTop = positionRef.current;
                    const birdBottom = positionRef.current + BIRD_HEIGHT;
                    const birdLeft = BIRD_X_POSITION;
                    const birdRight = BIRD_X_POSITION + BIRD_WIDTH;

                    const topPipeBottom = pipe.height; // Bottom edge of top pipe
                    
                    // UPDATED: Bottom pipe positioning using dynamic dimensions
                    // For small devices, we need to scale the pipe calculations
                    let bottomPipeHeight, bottomPipeTop;
                    
                    if (gameDimensions.isSmallDevice) {
                        // For small devices, scale the pipe calculations to match the actual rendered size
                        const scaleFactor = currentGameHeight / 570; // Scale factor from desktop to current device
                        const scaledPipeHeight = pipe.height * scaleFactor;
                        const scaledPipeGap = pipe.gap * scaleFactor;
                        
                        // Calculate bottom pipe dimensions for small devices
                        bottomPipeHeight = (480 * scaleFactor) - scaledPipeHeight - scaledPipeGap;
                        bottomPipeTop = currentGameHeight - bottomPipeHeight;
                    } else {
                        // Desktop calculation (original)
                        bottomPipeHeight = 480 - pipe.height - pipe.gap;
                        bottomPipeTop = currentGameHeight - bottomPipeHeight;
                    }
                    
                    const pipeLeft = pipe.x;
                    const pipeRight = pipe.x + pipeWidth;

                    // Check horizontal overlap - bird and pipe must overlap horizontally
                    const hasHorizontalOverlap = birdRight > pipeLeft && birdLeft < pipeRight;

                    // ENHANCED: Device-specific collision tolerance
                    let effectiveTolerance = dynamicTolerance;
                    if (gameDimensions.isSmallDevice) {
                        // Reduce tolerance for small devices for more precise collision detection
                        effectiveTolerance = Math.max(dynamicTolerance * 0.5, 3); // 50% of base tolerance, minimum 3px
                    }
                    
                    const touchesTopPipe = hasHorizontalOverlap && (birdTop + effectiveTolerance) < topPipeBottom;
                    
                    // ENHANCED: More precise bottom pipe detection for small devices
                    let touchesBottomPipe;
                    if (gameDimensions.isSmallDevice) {
                        // For small devices, use even more precise detection
                        const bottomTolerance = Math.max(effectiveTolerance * 0.7, 2); // 70% of effective tolerance, minimum 2px
                        touchesBottomPipe = hasHorizontalOverlap && (birdBottom - bottomTolerance) > bottomPipeTop;
                    } else {
                        touchesBottomPipe = hasHorizontalOverlap && (birdBottom - effectiveTolerance) > bottomPipeTop;
                    }
                    
                    const touchesCeiling = birdTop < 0; // Bird touches top of screen
                    
                    // Visual debugging
                    if (showDebug) {
                        // Get canvas context
                        const ctx = ctxRef.current;
                        if (!ctx) return;
                        
                        // Clear canvas before drawing
                        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                        
                        // Enhanced controls indicator with device info
                        ctx.font = "12px Arial";
                        ctx.fillStyle = "white";
                        ctx.fillText("SPACE: Jump | D: Debug | B: Bottom Pipes | C: All Collisions | +/-: Tolerance | R: Reset", 10, 15);
                        
                        // Device information
                        const deviceType = gameDimensions.isSmallDevice ? "SMALL DEVICE MODE - Enhanced Bottom Pipe Detection" : "LARGE DEVICE MODE";
                        ctx.fillStyle = gameDimensions.isSmallDevice ? "yellow" : "white";
                        ctx.fillText(`${deviceType} | Game: ${currentGameHeight}x${gameDimensions.width} | Ground: ${currentGroundHeight}px`, 10, 30);
                        
                        ctx.fillStyle = "white";
                        ctx.fillText(`Bird: (${Math.round(birdLeft)},${Math.round(birdTop)}) to (${Math.round(birdRight)},${Math.round(birdBottom)})`, 10, 45);
                        ctx.fillText(`Top Pipe Bottom: ${Math.round(topPipeBottom)} | Bottom Pipe Top: ${Math.round(bottomPipeTop)}`, 10, 60);
                        ctx.fillText(`Horizontal Overlap: ${hasHorizontalOverlap ? "YES" : "NO"}`, 10, 75);
                        ctx.fillText(`Top Collision: ${touchesTopPipe ? "YES" : "NO"} | Bottom Collision: ${touchesBottomPipe ? "YES" : "NO"}`, 10, 90);
                        
                        // Enhanced tolerance display for small devices
                        if (gameDimensions.isSmallDevice) {
                            const bottomTolerance = Math.max(effectiveTolerance * 0.7, 2);
                            ctx.fillText(`Effective Tolerance: ${effectiveTolerance}px | Bottom Tolerance: ${bottomTolerance}px`, 10, 105);
                            ctx.fillText(`Top Gap: ${Math.round(topPipeBottom - (birdTop + effectiveTolerance))} | Bottom Gap: ${Math.round((birdBottom - bottomTolerance) - bottomPipeTop)}`, 10, 120);
                        } else {
                            ctx.fillText(`Tolerance: ${effectiveTolerance}px | Top Gap: ${Math.round(topPipeBottom - (birdTop + effectiveTolerance))} | Bottom Gap: ${Math.round((birdBottom - effectiveTolerance) - bottomPipeTop)}`, 10, 105);
                        }
                        
                        ctx.fillText(`Actual Penetration: Top ${Math.round(topPipeBottom - birdTop)}px | Bottom ${Math.round(birdBottom - bottomPipeTop)}px`, 10, 135);
                        
                        // Bird hitbox - red if collision, green if safe
                        ctx.strokeStyle = (touchesTopPipe || touchesBottomPipe) ? "red" : "green";
                        ctx.lineWidth = 2;
                        ctx.strokeRect(birdLeft, birdTop, BIRD_WIDTH, BIRD_HEIGHT);
                        
                        // Draw pipes if they're near the bird
                        if (hasHorizontalOverlap) {
                            // Top pipe
                            ctx.fillStyle = touchesTopPipe ? "rgba(255, 0, 0, 0.5)" : "rgba(0, 255, 0, 0.3)";
                            ctx.fillRect(pipeLeft, 0, pipeWidth, topPipeBottom);
                            
                            // Bottom pipe
                            ctx.fillStyle = touchesBottomPipe ? "rgba(255, 0, 0, 0.5)" : "rgba(0, 255, 0, 0.3)";
                            ctx.fillRect(pipeLeft, bottomPipeTop, pipeWidth, GAME_HEIGHT - bottomPipeTop);
                            
                            // Pipe boundaries
                            ctx.strokeStyle = "white";
                            ctx.lineWidth = 2;
                            ctx.beginPath();
                            ctx.moveTo(pipeLeft, topPipeBottom);
                            ctx.lineTo(pipeRight, topPipeBottom);
                            ctx.moveTo(pipeLeft, bottomPipeTop);
                            ctx.lineTo(pipeRight, bottomPipeTop);
                            ctx.stroke();
                            
                            // Gap area
                            ctx.fillStyle = "rgba(255, 255, 0, 0.2)";
                            ctx.fillRect(pipeLeft, topPipeBottom, pipeWidth, pipe.gap);
                            
                            // ENHANCED VISUAL DEBUG - Show tolerance zones
                            // Top pipe tolerance zone
                            ctx.strokeStyle = "orange";
                            ctx.lineWidth = 1;
                            ctx.setLineDash([5, 5]);
                            ctx.beginPath();
                            ctx.moveTo(pipeLeft, topPipeBottom);
                            ctx.lineTo(pipeRight, topPipeBottom);
                            ctx.stroke();
                            
                            // Bottom pipe tolerance zone
                            ctx.beginPath();
                            ctx.moveTo(pipeLeft, bottomPipeTop);
                            ctx.lineTo(pipeRight, bottomPipeTop);
                            ctx.stroke();
                            ctx.setLineDash([]); // Reset line dash
                            
                            // Show bird collision boundaries with tolerance
                            ctx.strokeStyle = "cyan";
                            ctx.lineWidth = 1;
                            ctx.setLineDash([3, 3]);
                            // Bird top + tolerance line
                            ctx.beginPath();
                            ctx.moveTo(birdLeft - 10, birdTop + effectiveTolerance);
                            ctx.lineTo(birdRight + 10, birdTop + effectiveTolerance);
                            ctx.stroke();
                            // Bird bottom - tolerance line
                            ctx.beginPath();
                            ctx.moveTo(birdLeft - 10, birdBottom - effectiveTolerance);
                            ctx.lineTo(birdRight + 10, birdBottom - effectiveTolerance);
                            ctx.stroke();
                            ctx.setLineDash([]); // Reset line dash
                            
                            // Show exact pixel differences
                            ctx.font = "10px Arial";
                            ctx.fillStyle = "yellow";
                            ctx.fillText(`Exact diff: ${(birdBottom - effectiveTolerance - bottomPipeTop).toFixed(1)}px`, pipeLeft + 5, bottomPipeTop - 5);
                            
                            // Collision indicators
                            if (touchesTopPipe) {
                                ctx.font = "14px Arial";
                                ctx.fillStyle = "red";
                                ctx.fillText("TOP COLLISION", pipeLeft + 5, topPipeBottom - 10);
                            }
                            if (touchesBottomPipe) {
                                ctx.font = "14px Arial";
                                ctx.fillStyle = "red";
                                ctx.fillText("BOTTOM COLLISION", pipeLeft + 5, bottomPipeTop + 20);
                            }
                        }
                    }
                    
                    // Debug logging
                    if (DEBUG_MODE && hasHorizontalOverlap) {
                        console.log("===== COLLISION DEBUG =====");
                        console.log(`Bird: (${Math.round(birdLeft)},${Math.round(birdTop)}) to (${Math.round(birdRight)},${Math.round(birdBottom)})`);
                        console.log(`Top Pipe: (${Math.round(pipeLeft)},0) to (${Math.round(pipeRight)},${Math.round(topPipeBottom)})`);
                        console.log(`Bottom Pipe: (${Math.round(pipeLeft)},${Math.round(bottomPipeTop)}) to (${Math.round(pipeRight)},${GAME_HEIGHT})`);
                        console.log(`Horizontal Overlap: ${hasHorizontalOverlap}`);
                        console.log(`Collision Tolerance: ${effectiveTolerance}px`);
                        console.log(`PIPE CALCULATION DEBUG:`);
                        console.log(`  pipe.height: ${pipe.height}`);
                        console.log(`  pipe.gap (dynamic): ${pipe.gap} (original: ${PIPE_GAP})`);
                        console.log(`  GAME_HEIGHT: ${GAME_HEIGHT} (updated to match CSS)`);
                        console.log(`  Bottom pipe height (JSX): ${480 - pipe.height - pipe.gap}`);
                        console.log(`  Bottom pipe calculated top: ${bottomPipeTop}`);
                        console.log(`  Bottom pipe actual height: ${bottomPipeHeight}`);
                        console.log(`Top Collision: ${touchesTopPipe} (bird top + tolerance ${Math.round(birdTop + effectiveTolerance)} vs pipe bottom ${Math.round(topPipeBottom)})`);
                        console.log(`Bottom Collision: ${touchesBottomPipe} (bird bottom - tolerance ${Math.round(birdBottom - effectiveTolerance)} vs pipe top ${Math.round(bottomPipeTop)})`);
                        console.log(`Top Gap: ${Math.round(topPipeBottom - (birdTop + effectiveTolerance))}px | Bottom Gap: ${Math.round((birdBottom - effectiveTolerance) - bottomPipeTop)}px`);
                        console.log(`Final Result: ${(touchesTopPipe || touchesBottomPipe) ? "COLLISION" : "SAFE"}`);
                        console.log("==========================");
                    }
                    
                    // PRECISE COLLISION DETECTION - Only trigger when bird actually touches pipes
                    if (
                        allCollisionsEnabled && (
                            touchesTopPipe || 
                            (bottomPipeCollisionsEnabled && touchesBottomPipe) || 
                            touchesCeiling
                        )
                    ) {
                        // Log collision details only in debug mode
                        if (DEBUG_MODE) {
                            console.log("*** COLLISION DETECTED ***", {
                                touchesTopPipe,
                                touchesBottomPipe,
                                touchesCeiling,
                                birdPosition: { top: birdTop, bottom: birdBottom, left: birdLeft, right: birdRight },
                                pipePosition: { left: pipeLeft, right: pipeRight, topBottom: topPipeBottom, bottomTop: bottomPipeTop }
                            });
                        }
                        
                        handleGameOver();
                        return prevPipes;
                    }
                    
                    // Score update - only if past center of bird
                    if (!pipe.passed && pipe.x < BIRD_X_POSITION - (BIRD_WIDTH/2)) {
                        pipe.passed = true;
                        setScore(prev => prev + 1);
                        // Play ting sound when scoring
                        playTingSound();
                    }
                }
                
                return newPipes;
            });
            
            // Continue the loop
            if (!gameOver) {
                frameRef.current = requestAnimationFrame(runGameLoop);
            }
        };
        
        // Start the game loop
        frameRef.current = requestAnimationFrame(runGameLoop);
        
        // Start pipe generation with adjusted interval for Samsung Android
        const pipeInterval = isSamsungAndroid() ? 2000 : 1200; // Slower spawn rate for Samsung Android
        pipeIntervalRef.current = setInterval(spawnPipe, pipeInterval);
        
        // Cleanup
        return () => {
            cancelAnimationFrame(frameRef.current);
            clearInterval(pipeIntervalRef.current);
            clearInterval(speedIntervalRef.current);
        };
    }, [gameStarted, gameOver]);
    
    // Jump function
    const handleJump = () => {
        console.log("Jump called");
        
        if (gameOver) return;
        
        if (!gameStarted) {
            console.log("Starting game");
            setGameStarted(true);
            velocityRef.current = JUMP_FORCE;
            // Reset pipe speed when starting game
            setPipeSpeed(PIPE_SPEED_INITIAL);
            pipeSpeedRef.current = PIPE_SPEED_INITIAL;
            
            return;
        }
        
        // DIRECT JUMP - Update ref directly
        velocityRef.current = JUMP_FORCE;
        console.log("Jump force applied:", JUMP_FORCE);
        
        // Visual feedback
        if (birdRef.current) {
            birdRef.current.classList.add('jumping');
            setTimeout(() => {
                if (birdRef.current) birdRef.current.classList.remove('jumping');
            }, 300);
        }
    };
    
    // Spawn a pipe
    const spawnPipe = () => {
        if (!gameStarted || gameOver) return;
        
        const pipeGap = getDynamicPipeGap(score);
        
        // Generate top pipe height based on previous bottom pipe
        let topPipeHeight;
        let bottomPipeHeight;
        
        // Add 20% chance for tall bottom pipe
        const shouldGenerateTallBottomPipe = Math.random() < 0.2;
        
        if (prevBottomPipeHeight === 110) {
            // If previous bottom pipe was 110px, make this one less than 80px
            topPipeHeight = Math.random() * (300 - 250) + 250; // Generate high top pipe
            bottomPipeHeight = 480 - topPipeHeight - pipeGap; // This will result in small bottom pipe
        } else if (shouldGenerateTallBottomPipe) {
            // Generate a tall bottom pipe (200-240px)
            bottomPipeHeight = Math.random() * (240 - 200) + 200;
            topPipeHeight = 480 - bottomPipeHeight - pipeGap;
        } else {
            // Normal pipe generation
            topPipeHeight = Math.random() * (200 - 100) + 100;
            bottomPipeHeight = 480 - topPipeHeight - pipeGap;
            
            // Adjust if bottom pipe would be too small or too large
            if (bottomPipeHeight < 100) {
                topPipeHeight = 480 - pipeGap - 100;
                bottomPipeHeight = 100;
            } else if (bottomPipeHeight > 200) {
                topPipeHeight = 480 - pipeGap - 200;
                bottomPipeHeight = 200;
            }
        }
        
        // Store the current bottom pipe height for next spawn
        setPrevBottomPipeHeight(Math.round(bottomPipeHeight));
        
        setPipes(prev => [
            ...prev, 
            {
                x: 320,
                height: topPipeHeight,
                passed: false,
                gap: pipeGap
            }
        ]);
    };
    
    // Handle game over
    const handleGameOver = () => {
        setGameOver(true);
        
        // Play game over sound
        playGameOverSound();
        
        // Stop animations
        cancelAnimationFrame(frameRef.current);
        clearInterval(pipeIntervalRef.current);
        clearInterval(speedIntervalRef.current);
    };
    
    // Reset game
    const restartGame = () => {
        setBirdY(240);
        setBirdVelocity(0);
        setBirdRotation(0);
        setPipes([]);
        setScore(0);
        setPipeSpeed(PIPE_SPEED_INITIAL);
        setSpeedLevel(1.0); // Reset speed level to 1.0x
        setSpeedChanged(false); // Reset the speed change effect
        setGameOver(false);
        setGameStarted(false);
        
        // Reset refs
        positionRef.current = 240;
        velocityRef.current = 0;
        pipeSpeedRef.current = PIPE_SPEED_INITIAL;
    };

    // Enhanced keyboard controls listener 
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.code === 'Space' || e.key === ' ' || e.key === 'ArrowUp') {
                if (gameOver) {
                    // If game is over, restart the game
                    restartGame();
                } else {
                    // Otherwise, handle jump
                    handleJump();
                }
                e.preventDefault();
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [gameOver]); // Add gameOver as dependency

    // Handle share button click
    const handleShareClick = () => {
        setShowShareOptions(!showShareOptions);
    };

    // Toggle debug mode with 'D' key
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.code === 'KeyD') {
                setShowDebug(prev => !prev);
                console.log("Debug mode toggled:", !showDebug);
                
                // Set up canvas if turning on debug mode
                if (!showDebug && canvasRef.current) {
                    const canvas = canvasRef.current;
                    const gameDimensions = getGameDimensions();
                    canvas.width = gameDimensions.width; // Use dynamic width
                    canvas.height = gameDimensions.height; // Use dynamic height
                    ctxRef.current = canvas.getContext('2d');
                }
            }
            // Add 'B' key to toggle bottom pipe collisions
            if (e.code === 'KeyB') {
                setBottomPipeCollisionsEnabled(prev => !prev);
                console.log("Bottom pipe collisions toggled:", !bottomPipeCollisionsEnabled);
            }
            // Add 'C' key to toggle all collisions
            if (e.code === 'KeyC') {
                setAllCollisionsEnabled(prev => !prev);
                console.log("All collisions toggled:", !allCollisionsEnabled);
            }
            // Add 'T' key to test different collision calculations
            if (e.code === 'KeyT') {
                console.log("=== TESTING DIFFERENT COLLISION CALCULATIONS ===");
                // This will help us understand which calculation is correct
            }
            // Add '+' key to increase tolerance
            if (e.code === 'Equal' || e.code === 'NumpadAdd') {
                setDynamicTolerance(prev => {
                    const newTolerance = Math.min(prev + 2, 30);
                    console.log(`Collision tolerance increased to: ${newTolerance}px`);
                    return newTolerance;
                });
            }
            // Add '-' key to decrease tolerance
            if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
                setDynamicTolerance(prev => {
                    const newTolerance = Math.max(prev - 2, 0);
                    console.log(`Collision tolerance decreased to: ${newTolerance}px`);
                    return newTolerance;
                });
            }
            // Add 'R' key to reset tolerance to default
            if (e.code === 'KeyR') {
                setDynamicTolerance(15);
                console.log("Collision tolerance reset to 15px");
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showDebug, bottomPipeCollisionsEnabled, allCollisionsEnabled]);

    // iPad orientation alert effect
    useEffect(() => {
        const checkIPadOrientation = () => {
            if (isIPad() && isPortraitMode() && !gameOver) {
                setShowIPadAlert(true);
            } else {
                setShowIPadAlert(false);
            }
        };

        // Check on mount
        checkIPadOrientation();

        // Check on orientation change
        const handleOrientationChange = () => {
            // Small delay to ensure dimensions are updated
            setTimeout(checkIPadOrientation, 100);
        };

        window.addEventListener('resize', handleOrientationChange);
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('resize', handleOrientationChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, [gameOver]);

    return (
        <div 
            className="game-container" 
            style={{
                background: `linear-gradient(to bottom, #70c1ff 0%, #87ceeb 50%, #b2eaff 100%)`
            }}
        >
            {/* Background elements */}
            <div className="background-hills"></div>
            
            {/* Cloud layers */}
            <div className="cloud cloud-1"></div>
            <div className="cloud cloud-2"></div>
            <div className="cloud cloud-3"></div>
            <div className="cloud cloud-4"></div>
            <div className="cloud cloud-5"></div>
            
            <div className="game-content">
                <div className="ad-space-left">
                <div className="ad-space-left-top"></div>
                <div className="ad-space-left-down"></div>
                </div>
                <div className="game-area" onClick={handleJump}>
                    <div className="score-container">
                        <div className="score">{score}</div>
                        <div className={`speed ${speedChanged ? 'speed-highlight' : ''}`}>{speedLevel.toFixed(1)}x</div>
                    </div>
                    
                    {/* Debug canvas overlay */}
                    {showDebug && (
                        <canvas 
                            ref={canvasRef} 
                            className="debug-canvas"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                zIndex: 100
                            }}
                        />
                    )}
                    
                    {/* Instructions */}
                    {!gameStarted && !gameOver && (
                        <div className="instructions">
                            Click or press SPACE to start
                        </div>
                    )}

                    {/* iPad Orientation Alert */}
                    {showIPadAlert && (
                        <div className="ipad-alert">
                            <div className="ipad-alert-content">
                                <div className="rotate-icon">📱 ↻</div>
                                <h3>Better Experience on iPad!</h3>
                                <p>Please rotate your device to <strong>landscape mode</strong> for the best gaming experience.</p>
                                <div className="landscape-icon">📱</div>
                            </div>
                        </div>
                    )}
                    
                    <div
                        ref={birdRef}
                        className="bird"
                        style={{
                            top: `${birdY}px`,
                            transform: `rotate(${birdRotation}deg)`
                        }}
                    />
                    
                    {pipes.map((pipe, index) => {
                        return (
                            <React.Fragment key={index}>
                                <div
                                    className="pipe top"
                                    style={{
                                        left: `${pipe.x}px`,
                                        height: `${pipe.height}px`
                                    }}
                                />
                                <div
                                    className="pipe bottom"
                                    style={{
                                        left: `${pipe.x}px`,
                                        height: `${480 - pipe.height - pipe.gap}px`
                                    }}
                                />
                            </React.Fragment>
                        );
                    })}
                    
                    <div className="ground" />
                    
                    {/* Grass elements */}
                    <div className="grass">
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                        <div className="grass-blade"></div>
                    </div>
                    
                    {gameOver && (
                        <div className="game-over">
                            <h2>Game Over</h2>
                            
                            <div className="score-details">
                                <div className="score-item">
                                    <span className="label">Score</span>
                                    <span className="value">{score}</span>
                                </div>
                                <div className="score-item">
                                    <span className="label">Speed</span>
                                    <span className="value">{speedLevel.toFixed(1)}x</span>
                                </div>
                            </div>
                            
                            <button onClick={restartGame}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16" fill="currentColor" style={{ marginRight: "8px" }}>
                                    <path d="M463.5 224H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1c-87.5 87.5-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5z"/>
                                </svg>
                                Play Again
                            </button>
                            
                            <button onClick={handleShareClick} className="share-button">
                                {showShareOptions ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="16" height="16" fill="currentColor" style={{ marginRight: "8px" }}>
                                            <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
                                        </svg>
                                        Hide Share Options
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor" style={{ marginRight: "8px" }}>
                                            <path d="M352 224c53 0 96-43 96-96s-43-96-96-96s-96 43-96 96c0 4 .2 8 .7 11.9l-94.1 47C145.4 170.2 121.9 160 96 160c-53 0-96 43-96 96s43 96 96 96c25.9 0 49.4-10.2 66.6-26.9l94.1 47c-.5 3.9-.7 7.8-.7 11.9c0 53 43 96 96 96s96-43 96-96s-43-96-96-96c-25.9 0-49.4 10.2-66.6 26.9l-94.1-47c.5-3.9 .7-7.8 .7-11.9s-.2-8-.7-11.9l94.1-47C302.6 213.8 326.1 224 352 224z"/>
                                        </svg>
                                        Share My Score
                                    </>
                                )}
                            </button>
                            
                            {showShareOptions && (
                                <div className="share-options">
                                    <p>Share your achievement with friends!</p>
                                    <div className="share-buttons">
                                        <a 
                                            href={shareScore(score).whatsapp} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="share-link whatsapp"
                                        >
                                            {SocialIcons.whatsapp} WhatsApp
                                        </a>
                                        <a 
                                            href={shareScore(score).telegram} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="share-link telegram"
                                        >
                                            {SocialIcons.telegram} Telegram
                                        </a>
                                        <a 
                                            href={shareScore(score).twitter} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="share-link twitter"
                                        >
                                            {SocialIcons.twitter} Twitter
                                        </a>
                                        <a 
                                            href={shareScore(score).facebook} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="share-link facebook"
                                        >
                                            {SocialIcons.facebook} Facebook
                                        </a>
                                        <a 
                                            href={shareScore(score).email} 
                                            className="share-link email"
                                        >
                                            {SocialIcons.email} Email
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="ad-space-right"><div className="ad-space-left-top"></div>
                <div className="ad-space-left-down"></div></div>
                
                {/* How to Play section */}
                <div className="how-to-play">
                    <h3>How to Play</h3>
                    <p>
                        Navigate the bird through gaps between pipes without touching them. 
                        The longer you survive, the higher your score will be.
                    </p>
                    <ul>
                        <li>
                            <span className="key-instruction">Click</span> or press <span className="key-instruction">Spacebar</span> to make the bird flap upward
                        </li>
                        <li>
                            Navigate through the <span className="highlight">gaps</span> between pipes
                        </li>
                        <li>
                            Each pipe passed adds <span className="highlight">1 point</span> to your score
                        </li>
                        <li>
                            Every 20 points advances you to the next <span className="highlight">level</span> with narrower gaps
                        </li>
                        <li>
                            Game ends when the bird touches a pipe or the ground
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Game; 